import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axiosInstance from "../../../../axios";
import { useSelector } from "react-redux";
import moment from "moment";
import {
  FaBook,
  FaUsers,
  FaChevronLeft,
  FaChevronRight,
  FaCalendarAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaUserGraduate,
  FaPhoneAlt,
  FaArrowRight,
  FaHourglassHalf,
  FaSearch,
  FaList,
  FaTh,
  FaTimes,
} from "react-icons/fa";

export default function VerifyAttendance() {
  const user = useSelector((state) => state.users.user);
  const userId = user?.userId || user?.id;

  const [searchParams] = useSearchParams();
  const attendanceId = searchParams.get("attendanceId");

  // Main UI states
  const [courses, setCourses] = useState([]);
  const [courseSearchTerm, setCourseSearchTerm] = useState("");
  const [courseSortOrder, setCourseSortOrder] = useState("recent");
  const [batches, setBatches] = useState([]);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [attendance, setAttendance] = useState(null); // for direct email link

  const filteredAndSortedCourses = React.useMemo(() => {
    let result = courses || [];

    // 1. Filter by search term
    if (courseSearchTerm.trim()) {
      const term = courseSearchTerm.toLowerCase();
      result = result.filter(
        (c) =>
          c.CourseName?.toLowerCase().includes(term) ||
          String(c.ID).includes(term)
      );
    }

    // 2. Sort by selected order
    result = [...result].sort((a, b) => {
      if (courseSortOrder === "alpha-asc") {
        return (a.CourseName || "").localeCompare(b.CourseName || "");
      } else if (courseSortOrder === "alpha-desc") {
        return (b.CourseName || "").localeCompare(a.CourseName || "");
      } else if (courseSortOrder === "oldest") {
        return Number(a.ID) - Number(b.ID);
      } else {
        return Number(b.ID) - Number(a.ID);
      }
    });

    return result;
  }, [courses, courseSearchTerm, courseSortOrder]);

  const [viewMode, setViewMode] = useState("grid");
  const [coursesCurrentPage, setCoursesCurrentPage] = useState(1);
  const coursesPerPage = 8;

  useEffect(() => {
    setCoursesCurrentPage(1);
  }, [courseSearchTerm, courseSortOrder]);

  const paginatedCourses = React.useMemo(() => {
    const start = (coursesCurrentPage - 1) * coursesPerPage;
    return filteredAndSortedCourses.slice(start, start + coursesPerPage);
  }, [filteredAndSortedCourses, coursesCurrentPage, coursesPerPage]);

  const totalCoursePages = Math.ceil(filteredAndSortedCourses.length / coursesPerPage);

  // Selected entities
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));

  // Loaders
  const [loading, setLoading] = useState(true);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [markingStudentId, setMarkingStudentId] = useState(null);
  const [message, setMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch initial courses and general history
  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      try {
        setLoading(true);
        setMessage("");

        if (attendanceId) {
          // Direct verification view (e.g., from email link)
          const res = await axiosInstance.get(
            `/instructor/student-attendance-history/?attendanceId=${attendanceId}`
          );
          if (!res.data.error) {
            setAttendance(res.data.message);
          } else {
            setMessage("Invalid or missing attendance ID.");
          }
        } else {
          // Normal course list view
          const [coursesRes, historyRes] = await Promise.all([
            axiosInstance.get("/my-courses", { params: { perPage: 100 } }),
            axiosInstance.get(`/instructor/student-attendance-history/?instructorId=${userId}`),
          ]);

          if (!coursesRes.data.error) {
            setCourses(coursesRes.data.data?.data || []);
          }
          if (!historyRes.data.error) {
            setAttendanceHistory(historyRes.data.message || []);
          }
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setMessage("Error loading attendance metadata.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [attendanceId, userId]);

  // Fetch batches when a course is selected
  useEffect(() => {
    const fetchBatches = async () => {
      if (!selectedCourse) return;
      try {
        setBatchesLoading(true);
        const res = await axiosInstance.get("/admin/group-batch-list", {
          params: { courseId: selectedCourse.ID },
        });
        if (!res.data.error) {
          setBatches(res.data.data || []);
        } else {
          setBatches([]);
        }
      } catch (err) {
        console.error("Error fetching batches:", err);
        setBatches([]);
      } finally {
        setBatchesLoading(false);
      }
    };
    fetchBatches();
  }, [selectedCourse]);

  // Handle direct verification from link
  const handleDirectVerify = async () => {
    try {
      const res = await axiosInstance.post(`/instructor/verify-attendance`, {
        attendanceId,
        status: "Present",
        verified: 1,
      });
      if (!res.data.error) {
        setMessage("✅ Attendance verified successfully.");
        setAttendance((prev) => ({ ...prev, verified: true }));
      } else {
        setMessage("❌ Failed to verify attendance.");
      }
    } catch (err) {
      setMessage("❌ Server error while verifying attendance.");
    }
  };

  // Mark student attendance (create new or update existing)
  const handleMarkStudentAttendance = async (studentId, status) => {
    try {
      setMarkingStudentId(studentId);
      const res = await axiosInstance.post("/instructor/mark-student-attendance", {
        studentId,
        date: selectedDate,
        status,
        checkInTime: moment().format("HH:mm:ss"),
        courseId: selectedCourse.ID,
        groupId: selectedBatch.groupId,
      });

      if (!res.data.error) {
        // Refresh attendance history from backend to get the latest status
        const historyRes = await axiosInstance.get(
          `/instructor/student-attendance-history/?instructorId=${userId}`
        );
        if (!historyRes.data.error) {
          setAttendanceHistory(historyRes.data.message || []);
        }
      } else {
        alert("Failed to mark attendance.");
      }
    } catch (err) {
      console.error("Error marking student attendance:", err);
      alert("Server error while saving attendance.");
    } finally {
      setMarkingStudentId(null);
    }
  };
  const [checkedAbsentStudentIds, setCheckedAbsentStudentIds] = useState([]);
  const [showAttendanceTable, setShowAttendanceTable] = useState(false);
  const [showPresentModal, setShowPresentModal] = useState(false);
  const [showAbsentModal, setShowAbsentModal] = useState(false);
  const [modalSearchTerm, setModalSearchTerm] = useState("");

  useEffect(() => {
    setShowAttendanceTable(false);
    setShowPresentModal(false);
    setShowAbsentModal(false);
    setModalSearchTerm("");
  }, [selectedBatch, selectedCourse, selectedDate]);

  useEffect(() => {
    if (selectedBatch && selectedCourse && attendanceHistory) {
      const initialAbsentIds = (selectedBatch.students || [])
        .filter((student) => {
          const activeRecord = attendanceHistory.find(
            (record) =>
              record.studentId === student.id &&
              moment(record.date).format("YYYY-MM-DD") === selectedDate &&
              (record.courseId === selectedCourse.ID || !record.courseId)
          );
          return activeRecord && activeRecord.status?.toLowerCase() === "absent";
        })
        .map((student) => student.id);
      setCheckedAbsentStudentIds(initialAbsentIds);
    }
  }, [selectedBatch, selectedCourse, selectedDate, attendanceHistory]);

  const handleCheckboxChange = (studentId) => {
    setCheckedAbsentStudentIds((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSaveBatchAttendance = async () => {
    if (!selectedBatch || !selectedCourse) return;
    try {
      setLoading(true);
      const res = await axiosInstance.post("/instructor/submit-batch-attendance", {
        date: selectedDate,
        courseId: selectedCourse.ID,
        groupId: selectedBatch.groupId,
        absentStudentIds: checkedAbsentStudentIds,
      });

      if (!res.data.error) {
        // Refresh attendance history
        const historyRes = await axiosInstance.get(
          `/instructor/student-attendance-history/?instructorId=${userId}`
        );
        if (!historyRes.data.error) {
          setAttendanceHistory(historyRes.data.message || []);
        }
        alert("Attendance submitted successfully!");
        setShowAttendanceTable(false);
      } else {
        alert(res.data.message || "Failed to submit attendance.");
      }
    } catch (err) {
      console.error("Error submitting attendance:", err);
      alert("Server error while submitting attendance.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // 0. Individual verification card (Direct Email Link)
  if (attendanceId && attendance) {
    return (
      <div className="max-w-xl mx-auto p-6 mt-10 bg-white shadow-lg rounded-lg border">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Verify Student Attendance
        </h2>

        {message && (
          <div
            className={`mb-4 p-3 rounded-lg text-center ${
              message.includes("✅")
                ? "bg-green-50 text-green-700 border border-green-200"
                : "bg-red-50 text-red-700 border border-red-200"
            }`}
          >
            {message}
          </div>
        )}

        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <div className="flex justify-between">
            <span className="font-medium text-gray-600">Student Name:</span>
            <span className="text-gray-800 font-bold">
              {attendance.studentName || "N/A"} (ID: {attendance.studentId})
            </span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-600">Instructor ID:</span>
            <span className="text-gray-800">{attendance.instructorId}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-600">Date:</span>
            <span className="text-gray-800">
              {attendance.date ? moment(attendance.date).format("MMM DD, YYYY") : "N/A"}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-600">Check-in Time:</span>
            <span className="text-gray-800">{attendance.checkInTime || "N/A"}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-600">Status:</span>
            <span
              className={`font-medium ${
                attendance.status === "Present" ? "text-green-600" : "text-red-600"
              }`}
            >
              {attendance.status}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-600">Verified:</span>
            <span
              className={`font-medium ${
                attendance.verified ? "text-green-600" : "text-orange-600"
              }`}
            >
              {attendance.verified ? "Yes" : "No"}
            </span>
          </div>
        </div>

        {!attendance.verified && (
          <button
            onClick={handleDirectVerify}
            className="w-full mt-6 bg-[#264b82] text-white px-6 py-3 rounded-lg transition-colors font-medium hover:bg-blue-800"
          >
            Verify Attendance
          </button>
        )}

        <div className="mt-4 text-center">
          <Link to="?" className="text-sm text-blue-600 font-semibold hover:underline">
            ← Back to Course Selection
          </Link>
        </div>
      </div>
    );
  }

  // 1. Breadcrumbs helper
  const renderHeader = () => {
    return (
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 bg-slate-50 p-6 rounded-2xl border border-slate-200">
        <div>
          <h2 className="text-2xl font-black text-slate-800 tracking-tight">Student Attendance Portal</h2>
          <div className="flex items-center gap-2 text-sm text-slate-500 font-semibold mt-1">
            <span className="cursor-pointer hover:text-indigo-600 transition" onClick={() => { setSelectedCourse(null); setSelectedBatch(null); }}>Courses</span>
            {selectedCourse && (
              <>
                <span>/</span>
                <span className="cursor-pointer hover:text-indigo-600 transition" onClick={() => setSelectedBatch(null)}>{selectedCourse.CourseName}</span>
              </>
            )}
            {selectedBatch && (
              <>
                <span>/</span>
                <span className="text-indigo-600 font-bold">{selectedBatch.batchName}</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // 2. Render Courses List View
  if (!selectedCourse) {
    return (
      <div className="mx-auto p-4 md:p-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
        {renderHeader()}

        {courses.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-300">
            <FaBook className="mx-auto text-4xl text-slate-300 mb-4" />
            <p className="text-slate-500 font-bold">No active courses assigned to your account.</p>
          </div>
        ) : (
          <>
            {/* Course Search & Filter Bar */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
              <div className="relative w-full md:max-w-md">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400">
                  <FaSearch className="w-3.5 h-3.5" />
                </span>
                <input
                  type="text"
                  placeholder="Search courses by name or ID..."
                  value={courseSearchTerm}
                  onChange={(e) => setCourseSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl placeholder:text-slate-400 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition font-medium"
                />
              </div>

              <div className="flex flex-wrap items-center gap-4 w-full md:w-auto justify-end">
                {/* View Mode Toggle */}
                <div className="flex bg-slate-100 p-1 rounded-xl">
                  <button
                    onClick={() => setViewMode("normal")}
                    title="Normal (List) View"
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                      viewMode === "normal"
                        ? "bg-white text-slate-800 shadow-sm"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    <FaList className="w-3.5 h-3.5" /> List
                  </button>
                  <button
                    onClick={() => setViewMode("grid")}
                    title="Grid View"
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                      viewMode === "grid"
                        ? "bg-white text-slate-800 shadow-sm"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    <FaTh className="w-3.5 h-3.5" /> Grid
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <label className="text-xs font-bold text-slate-500 uppercase whitespace-nowrap">Sort By:</label>
                  <select
                    value={courseSortOrder}
                    onChange={(e) => setCourseSortOrder(e.target.value)}
                    className="px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-bold focus:outline-none focus:border-indigo-400 cursor-pointer"
                  >
                    <option value="recent">Recently Added (ID Desc)</option>
                    <option value="oldest">Oldest (ID Asc)</option>
                    <option value="alpha-asc">Alphabetical (A - Z)</option>
                    <option value="alpha-desc">Alphabetical (Z - A)</option>
                  </select>
                </div>
              </div>
            </div>

            {filteredAndSortedCourses.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-300">
                <FaSearch className="mx-auto text-4xl text-slate-300 mb-4" />
                <p className="text-slate-500 font-bold">No courses match your search criteria.</p>
              </div>
            ) : (
              <>
                {viewMode === "grid" ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {paginatedCourses.map((course) => (
                      <div
                        key={course.ID}
                        onClick={() => setSelectedCourse(course)}
                        className="group relative cursor-pointer bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col justify-between"
                      >
                        <div>
                          <div className="h-32 w-full bg-gradient-to-br from-indigo-400 to-indigo-700 flex items-center justify-center text-white relative">
                            <FaBook className="text-5xl opacity-50 group-hover:scale-110 transition-transform duration-300" />
                            <span className="absolute bottom-3 right-3 text-xs bg-dark/20 backdrop-blur-md px-3 py-1 rounded-full font-bold">
                              ID: {course.ID}
                            </span>
                          </div>
                          <div className="p-5">
                            <h3 className="text-base font-bold text-slate-800 leading-snug group-hover:text-indigo-600 transition">
                              {course.CourseName}
                            </h3>
                          </div>
                        </div>
                        <div className="p-5 pt-0 flex justify-between items-center text-sm font-bold text-indigo-600">
                          <span>View Batches</span>
                          <FaArrowRight className="group-hover:translate-x-0.5 transition-transform" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    {paginatedCourses.map((course) => (
                      <div
                        key={course.ID}
                        onClick={() => setSelectedCourse(course)}
                        className="group relative cursor-pointer bg-white rounded-2xl border border-slate-200 p-4 hover:shadow-lg transition-all duration-300 flex flex-col sm:flex-row items-center justify-between gap-4"
                      >
                        <div className="flex items-center gap-4 w-full">
                          <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center text-white flex-shrink-0">
                            <FaBook className="text-2xl" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-base font-extrabold text-slate-800 leading-snug group-hover:text-indigo-600 transition truncate">
                              {course.CourseName}
                            </h3>
                            <span className="text-xs font-bold text-slate-400">ID: {course.ID}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm font-bold text-indigo-600 flex-shrink-0 w-full sm:w-auto justify-end">
                          <span>View Batches</span>
                          <FaArrowRight className="group-hover:translate-x-0.5 transition-transform" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pagination Controls */}
                {totalCoursePages > 1 && (
                  <div className="flex justify-center items-center gap-3 mt-8 pt-6 border-t border-slate-200">
                    <button
                      disabled={coursesCurrentPage === 1}
                      onClick={() => setCoursesCurrentPage((p) => p - 1)}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm bg-white border border-slate-200 rounded-xl text-slate-700 font-bold hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FaChevronLeft className="w-3 h-3" /> Previous
                    </button>
                    <span className="text-sm font-extrabold text-slate-700">
                      Page {coursesCurrentPage} of {totalCoursePages}
                    </span>
                    <button
                      disabled={coursesCurrentPage === totalCoursePages}
                      onClick={() => setCoursesCurrentPage((p) => p + 1)}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm bg-white border border-slate-200 rounded-xl text-slate-700 font-bold hover:bg-slate-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next <FaChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    );
  }

  // 3. Render Batches List View
  if (selectedCourse && !selectedBatch) {
    return (
      <div className="mx-auto p-4 md:p-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
        {renderHeader()}

        <button
          onClick={() => setSelectedCourse(null)}
          className="flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-slate-900 mb-6 bg-slate-100 px-4 py-2 rounded-xl transition"
        >
          <FaChevronLeft /> Back to Courses
        </button>

        {batchesLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : batches.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-300">
            <FaUsers className="mx-auto text-4xl text-slate-300 mb-4" />
            <p className="text-slate-500 font-bold">No active group batches found for this course.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {batches.map((batch) => {
              const studentCount = batch.students?.length || 0;
              return (
                <div
                  key={batch.groupId}
                  onClick={() => setSelectedBatch(batch)}
                  className="group relative cursor-pointer bg-white rounded-2xl border border-slate-200 overflow-hidden p-6 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-indigo-50 text-indigo-600 p-3 rounded-xl">
                        <FaUsers className="text-xl" />
                      </div>
                      <div>
                        <h4 className="text-base font-extrabold text-slate-800 leading-snug">
                          {batch.batchName}
                        </h4>
                        <span className="text-xs font-bold text-slate-400">ID: {batch.groupId}</span>
                      </div>
                    </div>

                    <div className="space-y-2 mb-6">
                      <div className="flex justify-between text-xs font-bold text-slate-500">
                        <span>Students Enrolled:</span>
                        <span className="text-slate-800">{studentCount} students</span>
                      </div>
                      {batch.startDate && (
                        <div className="flex justify-between text-xs font-bold text-slate-500">
                          <span>Timeline:</span>
                          <span className="text-slate-800">
                            {moment(batch.startDate).format("MMM YYYY")} - {batch.endDate ? moment(batch.endDate).format("MMM YYYY") : "Present"}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <button className="w-full bg-slate-50 text-indigo-600 border border-indigo-100 hover:bg-indigo-50 py-2.5 rounded-xl text-sm font-bold transition flex items-center justify-center gap-2">
                    Manage Attendance <FaArrowRight style={{ fontSize: "10px" }} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // 4. Render Batch Attendance Student Marking View
  const students = selectedBatch.students || [];

  const filteredStudents = students.filter((student) => {
    const term = searchTerm.toLowerCase();
    const name = (student.name || "").toLowerCase();
    const id = String(student.id || "");
    return name.includes(term) || id.includes(term);
  });

  return (
    <div className="mx-auto p-4 md:p-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {renderHeader()}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4 flex-1">
          <button
            onClick={() => setSelectedBatch(null)}
            className="flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-slate-900 bg-slate-100 px-4 py-2 rounded-xl transition flex-shrink-0"
          >
            <FaChevronLeft /> Back to Batches
          </button>
        </div>
      </div>

      {/* Blueprint Statistics Panel */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        {/* Date Selector */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-bold text-slate-400 uppercase">Class Date</label>
          <div className="flex items-center gap-2.5 bg-slate-50 border border-slate-200 px-4 h-12 rounded-xl">
            <FaCalendarAlt className="text-indigo-500 flex-shrink-0" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="text-sm font-extrabold text-slate-700 outline-none bg-transparent cursor-pointer w-full"
            />
          </div>
        </div>

        {/* Total Students Count */}
        <div 
          onClick={() => setShowAttendanceTable(true)}
          className="flex flex-col gap-1 cursor-pointer group"
        >
          <label className="text-xs font-bold text-slate-400 uppercase group-hover:text-indigo-500 transition">No. Student</label>
          <div className="bg-slate-50 border border-slate-200 group-hover:border-indigo-300 px-4 h-12 rounded-xl flex items-center justify-between transition shadow-sm group-hover:shadow-md">
            <span className="text-lg font-black text-slate-800 group-hover:text-indigo-600 transition">{students.length}</span>
            <span className="text-xs font-bold text-slate-400">Click to mark absent</span>
          </div>
        </div>

        {/* Present Students Count */}
        <div 
          onClick={() => setShowPresentModal(true)}
          className="flex flex-col gap-1 cursor-pointer group"
        >
          <label className="text-xs font-bold text-slate-400 uppercase group-hover:text-green-600 transition">Present Stu Count</label>
          <div className="bg-green-50/50 border border-green-100 group-hover:border-green-300 px-4 h-12 rounded-xl flex items-center justify-between transition shadow-sm group-hover:shadow-md">
            <span className="text-lg font-black text-green-700 group-hover:text-green-800 transition">
              {students.length - checkedAbsentStudentIds.length}
            </span>
            <span className="text-xs font-bold text-green-600/70">Click to view list</span>
          </div>
        </div>

        {/* Absent Students Count */}
        <div 
          onClick={() => setShowAbsentModal(true)}
          className="flex flex-col gap-1 cursor-pointer group"
        >
          <label className="text-xs font-bold text-slate-400 uppercase group-hover:text-red-650 transition">Absent Stu Count</label>
          <div className="bg-red-50/50 border border-red-100 group-hover:border-red-300 px-4 h-12 rounded-xl flex items-center justify-between transition shadow-sm group-hover:shadow-md">
            <span className="text-lg font-black text-red-700 group-hover:text-red-800 transition">{checkedAbsentStudentIds.length}</span>
            <span className="text-xs font-bold text-red-600/70">Click to view list</span>
          </div>
        </div>
      </div>

      {/* <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4 flex-1">
          <button
            onClick={() => setSelectedBatch(null)}
            className="flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-slate-900 bg-slate-100 px-4 py-2 rounded-xl transition flex-shrink-0"
          >
            <FaChevronLeft /> Back to Batches
          </button>
        </div>
      </div> */}

      <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-300">
        <FaUserGraduate className="mx-auto text-4xl text-slate-300 mb-4" />
        <p className="text-slate-500 font-extrabold mb-2 text-lg">Attendance Dashboard</p>
        <p className="text-slate-400 text-sm font-bold max-w-md mx-auto leading-relaxed">
          • Click on <span className="text-indigo-600 font-extrabold cursor-pointer hover:underline" onClick={() => setShowAttendanceTable(true)}>No. Student</span> to open the attendance marking checklist. <br/>
          • Click on <span className="text-green-600 font-extrabold cursor-pointer hover:underline" onClick={() => setShowPresentModal(true)}>Present Stu Count</span> to view present students. <br/>
          • Click on <span className="text-red-600 font-extrabold cursor-pointer hover:underline" onClick={() => setShowAbsentModal(true)}>Absent Stu Count</span> to view absent students.
        </p>
      </div>

      {/* 1. Absent Selection (Marking) Modal */}
      {showAttendanceTable && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <div>
                <h3 className="text-xl font-black text-slate-800">Mark Absent Students</h3>
                <p className="text-xs font-bold text-slate-400 mt-1">Select students who are absent for this class</p>
              </div>
              <button 
                onClick={() => { setShowAttendanceTable(false); setSearchTerm(""); }}
                className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Searchbar */}
            <div className="p-6 pb-2">
              <div className="relative w-full">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400">
                  <FaSearch className="w-3.5 h-3.5" />
                </span>
                <input
                  type="text"
                  placeholder="Search student by name or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl placeholder:text-slate-400 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition font-medium"
                />
              </div>
            </div>

            {/* Modal Body Checklist */}
            <div className="flex-1 overflow-y-auto p-6 pt-2">
              {filteredStudents.length === 0 ? (
                <div className="text-center py-12 text-slate-400 font-bold italic">
                  No matching students found.
                </div>
              ) : (
                <div className="divide-y divide-slate-100 border border-slate-200 rounded-2xl overflow-hidden bg-slate-50/30">
                  {filteredStudents.map((student) => {
                    const isCheckedAbsent = checkedAbsentStudentIds.includes(student.id);
                    const activeRecord = attendanceHistory.find(
                      (record) =>
                        record.studentId === student.id &&
                        moment(record.date).format("YYYY-MM-DD") === selectedDate &&
                        (record.courseId === selectedCourse.ID || !record.courseId)
                    );
                    const isPending = activeRecord?.status?.toLowerCase() === "pending";

                    return (
                      <div key={student.id} className="flex items-center justify-between p-4 bg-white hover:bg-slate-50 transition">
                        <div className="flex items-center gap-3.5">
                          <input
                            type="checkbox"
                            checked={isCheckedAbsent}
                            onChange={() => handleCheckboxChange(student.id)}
                            className="w-5 h-5 rounded border-slate-300 text-red-650 focus:ring-red-400 cursor-pointer"
                          />
                          <div>
                            <div className="text-sm font-extrabold text-slate-800">{student.name}</div>
                            <div className="text-xs font-bold text-slate-400">ID: {student.id}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isPending && (
                            <span className="inline-flex px-2 py-0.5 text-[10px] font-bold bg-orange-50 text-orange-600 rounded border border-orange-100">
                              Pending check-in
                            </span>
                          )}
                          {isCheckedAbsent ? (
                            <span className="text-xs font-extrabold text-red-600">Absent</span>
                          ) : (
                            <span className="text-xs font-extrabold text-green-600">Present</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer (Submit and Cancel buttons) */}
            <div className="p-6 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3 rounded-b-3xl">
              <button
                onClick={() => { setShowAttendanceTable(false); setSearchTerm(""); }}
                className="px-5 py-2.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold rounded-xl text-sm transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveBatchAttendance}
                className="px-8 py-2.5 bg-[#264b82] hover:bg-blue-800 text-white font-extrabold rounded-xl text-sm transition shadow-md"
              >
                Submit Attendance
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. Present Students List Modal */}
      {showPresentModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <div>
                <h3 className="text-lg font-black text-slate-800">Present Students ({students.length - checkedAbsentStudentIds.length})</h3>
                <p className="text-xs font-bold text-slate-400 mt-1">Students marked present for this session</p>
              </div>
              <button 
                onClick={() => { setShowPresentModal(false); setModalSearchTerm(""); }}
                className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Searchbar */}
            <div className="p-6 pb-2">
              <div className="relative w-full">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400">
                  <FaSearch className="w-3.5 h-3.5" />
                </span>
                <input
                  type="text"
                  placeholder="Search present student..."
                  value={modalSearchTerm}
                  onChange={(e) => setModalSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl placeholder:text-slate-400 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition font-medium"
                />
              </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-6 pt-2">
              {(() => {
                const presentStudentsList = students.filter(
                  (s) => !checkedAbsentStudentIds.includes(s.id) &&
                         ((s.name || "").toLowerCase().includes(modalSearchTerm.toLowerCase()) ||
                          String(s.id).includes(modalSearchTerm))
                );

                if (presentStudentsList.length === 0) {
                  return (
                    <div className="text-center py-8 text-slate-400 font-bold italic">
                      No present students found.
                    </div>
                  );
                }

                return (
                  <div className="space-y-3">
                    {presentStudentsList.map((student) => {
                      const initials = student.name
                        ? student.name.split(" ").map((n) => n[0]).join("").toUpperCase().substring(0, 2)
                        : "ST";
                      return (
                        <div key={student.id} className="flex items-center gap-3 p-3 bg-slate-50/50 rounded-xl border border-slate-100">
                          <div className="w-9 h-9 rounded-full bg-green-105 text-green-700 flex items-center justify-center font-extrabold text-xs">
                            {initials}
                          </div>
                          <div>
                            <div className="text-sm font-extrabold text-slate-800">{student.name}</div>
                            <div className="text-xs font-bold text-slate-400">ID: {student.id}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-slate-100 bg-slate-50 flex items-center justify-end rounded-b-3xl">
              <button
                onClick={() => { setShowPresentModal(false); setModalSearchTerm(""); }}
                className="px-6 py-2 bg-slate-800 hover:bg-slate-900 text-white font-extrabold rounded-xl text-sm transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 3. Absent Students List Modal */}
      {showAbsentModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <div>
                <h3 className="text-lg font-black text-slate-800">Absent Students ({checkedAbsentStudentIds.length})</h3>
                <p className="text-xs font-bold text-slate-400 mt-1">Students marked absent for this session</p>
              </div>
              <button 
                onClick={() => { setShowAbsentModal(false); setModalSearchTerm(""); }}
                className="p-2 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Searchbar */}
            <div className="p-6 pb-2">
              <div className="relative w-full">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400">
                  <FaSearch className="w-3.5 h-3.5" />
                </span>
                <input
                  type="text"
                  placeholder="Search absent student..."
                  value={modalSearchTerm}
                  onChange={(e) => setModalSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl placeholder:text-slate-400 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 transition font-medium"
                />
              </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-6 pt-2">
              {(() => {
                const absentStudentsList = students.filter(
                  (s) => checkedAbsentStudentIds.includes(s.id) &&
                         ((s.name || "").toLowerCase().includes(modalSearchTerm.toLowerCase()) ||
                          String(s.id).includes(modalSearchTerm))
                );

                if (absentStudentsList.length === 0) {
                  return (
                    <div className="text-center py-8 text-slate-400 font-bold italic">
                      No absent students found.
                    </div>
                  );
                }

                return (
                  <div className="space-y-3">
                    {absentStudentsList.map((student) => {
                      const initials = student.name
                        ? student.name.split(" ").map((n) => n[0]).join("").toUpperCase().substring(0, 2)
                        : "ST";
                      return (
                        <div key={student.id} className="flex items-center gap-3 p-3 bg-slate-50/50 rounded-xl border border-slate-100">
                          <div className="w-9 h-9 rounded-full bg-red-100 text-red-700 flex items-center justify-center font-extrabold text-xs">
                            {initials}
                          </div>
                          <div>
                            <div className="text-sm font-extrabold text-slate-800">{student.name}</div>
                            <div className="text-xs font-bold text-slate-400">ID: {student.id}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-slate-100 bg-slate-50 flex items-center justify-end rounded-b-3xl">
              <button
                onClick={() => { setShowAbsentModal(false); setModalSearchTerm(""); }}
                className="px-6 py-2 bg-slate-800 hover:bg-slate-900 text-white font-extrabold rounded-xl text-sm transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
